'use client'

import React from 'react'

import { Box, SystemStyleObject, Text, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { IconButton } from '../Button/IconButton'
import { DownloadIcon } from '../Icon/DownloadIcon'
import { Spinner } from '../Spinner'
import {
  fileThumbnailBodyStyles,
  fileThumbnailContainerStyles,
  fileThumbnailNameStyles,
  fileThumbnailPreviewImageStyles,
  fileThumbnailTypeLabelStyles,
} from './FileThumbnail.styles'
import { getExtension, getFileTypeIcon } from './fileTypeIcon'

export type FileThumbnailProps = {
  /** File name; the type label and glyph derive from its extension unless `extension` is set. */
  name: string
  /** When set, renders this image (20×20) as the leading visual instead of a file-type glyph. */
  previewSrc?: string
  /** Explicit extension override — e.g. derived from mimeType when the name has none. */
  extension?: string
  colorScheme?: ColorScheme
  /** Card click. Falls back to `onDownload` when omitted. */
  onClick?: () => void
  /** Renders the trailing download button; its click does not bubble to the card. */
  onDownload?: () => void
  isDownloading?: boolean
  width?: string
  containerStyleOverrides?: SystemStyleObject
}

/**
 * Row card for a file attachment: leading image thumb or file-type glyph,
 * name + uppercase type label, optional trailing download button. Replaces the
 * legacy feed image/pdf thumbnails and the web-app's Chakra v1 FileThumbnail
 * (ENG-32107). Glyphs are monochrome token-colored per the rebrand.
 */
export const FileThumbnail = ({
  name,
  previewSrc,
  extension,
  colorScheme = 'dark',
  onClick,
  onDownload,
  isDownloading,
  width = '230px',
  containerStyleOverrides,
}: FileThumbnailProps) => {
  const resolvedExtension = (extension ?? getExtension(name)).toLowerCase()
  const typeLabel = resolvedExtension ? resolvedExtension.toUpperCase() : 'FILE'
  const handleCardClick = onClick ?? onDownload

  const handleDownloadClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDownload?.()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCardClick?.()
    }
  }

  return (
    <chakra.div
      data-testid='fileThumbnail'
      onClick={handleCardClick}
      // The card stays a div (not a button) so the nested download button is
      // valid markup; role/tabIndex keep the clickable card keyboardable.
      onKeyDown={handleCardClick ? handleKeyDown : undefined}
      role={handleCardClick ? 'button' : undefined}
      tabIndex={handleCardClick ? 0 : undefined}
      {...fileThumbnailContainerStyles(
        colorScheme,
        Boolean(handleCardClick),
        width
      )}
      {...containerStyleOverrides}
    >
      {previewSrc ? (
        <chakra.img
          alt={name}
          src={previewSrc}
          {...fileThumbnailPreviewImageStyles}
        />
      ) : (
        getFileTypeIcon(resolvedExtension, colorScheme)
      )}
      <Box {...fileThumbnailBodyStyles}>
        <Text truncate {...fileThumbnailNameStyles}>
          {name}
        </Text>
        <Text {...fileThumbnailTypeLabelStyles(colorScheme)}>{typeLabel}</Text>
      </Box>
      {onDownload && (
        <IconButton
          aria-label={`Download ${name}`}
          colorScheme={colorScheme}
          disabled={isDownloading}
          icon={
            isDownloading ? (
              <Spinner borderWidth='2px' colorScheme={colorScheme} size='sm' />
            ) : (
              <DownloadIcon size={16} />
            )
          }
          onClick={handleDownloadClick}
        />
      )}
    </chakra.div>
  )
}
